import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CircleUserRound,
  HandCoins,
  LayoutDashboard,
  Languages,
  Menu,
  MessageCircle,
  Compass,
  TicketPercent,
  X,
  type LucideIcon,
} from "lucide-react";
import { changeLanguage } from "@/services/slices/setting.slice";
import AppLogo from "@/components/AppLogo";

type BusinessNavItem = {
  to: string;
  label: string;
  icon?: LucideIcon;
  alsoActive?: string[];
};

const NAV_ITEMS: BusinessNavItem[] = [
  { to: "/portal/dashboard", label: "Dashboard" },
  { to: "/portal/coupons", label: "Coupons" },
  { to: "/portal/sponsors", label: "Sponsors" },
  { to: "/portal/activities", label: "Activities" },
  { to: "/portal/account", label: "Account" },
  // The chat threads live under `/portal/chat/:chatRoomId` but belong to the
  // Messages tab.
  {
    to: "/portal/messages",
    label: "Messages",
    icon: MessageCircle,
    alsoActive: ["/portal/chat"],
  },
];

type BusinessMobileNavItem = BusinessNavItem & { icon: LucideIcon };

/** Drawer order: section links, Messages, then Account (mirrors student shell). */
const MOBILE_MENU_ITEMS: BusinessMobileNavItem[] = [
  { to: "/portal/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/portal/coupons", label: "Coupons", icon: TicketPercent },
  { to: "/portal/sponsors", label: "Sponsors", icon: HandCoins },
  { to: "/portal/activities", label: "Activities", icon: Compass },
  {
    to: "/portal/messages",
    label: "Messages",
    icon: MessageCircle,
    alsoActive: ["/portal/chat"],
  },
  { to: "/portal/account", label: "Account", icon: CircleUserRound },
];

const isActivePath = (pathname: string, item: BusinessNavItem) =>
  [item.to, ...(item.alsoActive ?? [])].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

/**
 * Business shell: guards `/portal/*` routes — only a logged-in business user
 * may enter. Renders the top navbar + content outlet.
 */
const BusinessLayout: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { loggedIn, user_auth } = useSelector((state: any) => state.auth);
  const language = useSelector((state: any) => state.setting.language);
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleLanguage = () => {
    dispatch(
      changeLanguage({ language: language === "zh_hant" ? "en" : "zh_hant" }),
    );
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  if (!loggedIn || user_auth !== "business") {
    return <Navigate to="/business/login" replace />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f5f5f5]">
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white">
        <div className="relative mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link to="/portal/dashboard" className="flex shrink-0 items-center gap-2">
            <AppLogo size={36} />
            <span className="hidden text-xl font-bold text-[#333] sm:inline">
              Uninfo<span className="text-[#ffa217]">HK</span>
            </span>
          </Link>

          <nav className="hidden min-w-0 flex-1 items-center justify-end gap-1 xl:flex">
            {NAV_ITEMS.map((item) => {
              const isActive = isActivePath(pathname, item);
              if (item.icon) {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-label={t(item.label)}
                    title={t(item.label)}
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition ${
                      isActive
                        ? "bg-[#fff3e0] text-[#ffa217]"
                        : "text-[#ffa217] hover:bg-[#fff3e0]"
                    }`}
                  >
                    <Icon size={19} />
                  </Link>
                );
              }
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? "bg-[#fff3e0] font-semibold text-[#ffa217]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-[#ffa217]"
                  }`}
                >
                  {t(item.label)}
                </Link>
              );
            })}

            <button
              type="button"
              onClick={toggleLanguage}
              className="shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
            >
              {language === "zh_hant" ? "EN" : "繁體"}
            </button>
          </nav>

          <div className="flex shrink-0 items-center gap-2 xl:hidden">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={t("Menu")}
              title={t("Menu")}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-600 transition hover:bg-gray-50 hover:text-[#ffa217]"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {menuOpen && (
            <div
              role="menu"
              aria-label={t("Menu")}
              className="absolute right-3 top-full z-50 w-56 rounded-b-2xl border border-t-0 border-gray-100 bg-white py-2 shadow-xl xl:hidden"
            >
              {MOBILE_MENU_ITEMS.map((item) => {
                const Icon = item.icon;
                const isActive = isActivePath(pathname, item);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition ${
                      isActive
                        ? "bg-[#fff3e0] font-semibold text-[#ffa217]"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Icon size={17} className="shrink-0 text-[#ffa217]" />
                    {t(item.label)}
                  </Link>
                );
              })}

              <button
                type="button"
                role="menuitem"
                onClick={toggleLanguage}
                className="mt-1 flex w-full items-center gap-2.5 border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
              >
                <Languages size={17} className="shrink-0 text-[#ffa217]" />
                <span className="ml-auto text-sm font-semibold text-[#ffa217]">
                  {language === "zh_hant" ? "EN" : "繁體"}
                </span>
              </button>
            </div>
          )}
        </div>
      </header>

      {menuOpen && (
        <div
          className="fixed inset-0 z-30 xl:hidden"
          aria-hidden="true"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default BusinessLayout;
